/**
 * ``Promise.allSettled`` with a cap on how many calls run at once.
 *
 * Every item settles on its own — a rejection never cancels the rest — and
 * the results keep the input order. Bulk actions on the video grid use it so
 * archiving 24 videos is 24 independent requests, but never 24 at the same
 * time against a backend that deletes folders on disk for each one.
 */
export async function mapSettled<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array<PromiseSettledResult<R>>(items.length)
  let next = 0

  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next++
      try {
        results[index] = { status: 'fulfilled', value: await fn(items[index] as T, index) }
      } catch (reason: unknown) {
        results[index] = { status: 'rejected', reason }
      }
    }
  }

  const workers = Math.max(1, Math.min(limit, items.length))
  await Promise.all(Array.from({ length: workers }, () => worker()))
  return results
}
